import React, { useCallback } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { QueryEntity } from "../../domain/entities/QueryEntity";
import { getRelativeTime } from "../../../../utils";

interface QueriesListForDocumentProps {
  title?: string;
  queries: QueryEntity[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onQueryClick: (queryId: number) => void;
}

const getStatusVariant = (status: string) => {
  switch (status.toLowerCase()) {
    case "open":
      return "warning";
    case "resolved":
      return "success";
    case "critical":
      return "error";
    default:
      return "neutral";
  }
};

const getPriorityVariant = (priority: string) => {
  switch (priority.toLowerCase()) {
    case "high":
      return "error";
    case "medium":
      return "warning";
    case "low":
      return "neutral";
    default:
      return "neutral";
  }
};

const QueriesListForDocument: React.FC<QueriesListForDocumentProps> = ({
  title = "Queries",
  queries,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onQueryClick,
}) => {
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const distanceToBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (distanceToBottom < 100 && hasMore && !loading && !loadingMore) {
        onLoadMore();
      }
    },
    [hasMore, loading, loadingMore, onLoadMore]
  );

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {title}
        </span>
        {queries.length > 0 && (
          <Badge variant="warning" icon={undefined}>
            {queries.length}
          </Badge>
        )}
      </div>

      <div
        className="flex w-full flex-col items-start gap-3 overflow-y-auto max-h-[360px]"
        onScroll={handleScroll}
      >
        {loading ? (
          <div className="flex w-full items-center justify-center py-4">
            <span className="text-body font-body text-neutral-500">
              Loading queries...
            </span>
          </div>
        ) : queries.length === 0 ? (
          <div className="flex w-full items-center justify-center py-4">
            <span className="text-body font-body text-neutral-500">
              No queries yet
            </span>
          </div>
        ) : (
          queries.map((query) => (
            <div
              key={query.queryId}
              className="flex w-full items-start gap-3 border-b border-solid border-neutral-100 pb-3 last:border-b-0 last:pb-0"
              onClick={() => onQueryClick(query.queryId)}
            >
              <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-warning-100">
                <span className="text-caption-bold font-caption-bold text-warning-600">
                  Q
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  {query.title}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {query.description || "No description provided"}
                </span>
                <div className="flex items-center gap-2">
                  <Badge variant={getStatusVariant(query.status)}>
                    {query.status.toUpperCase()}
                  </Badge>
                  <Badge variant={getPriorityVariant(query.priority)}>
                    {query.priority.toUpperCase()}
                  </Badge>
                  <span className="text-caption font-caption text-neutral-400">
                    {getRelativeTime(query.createdAt.toISOString())}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
        {loadingMore && (
          <div className="flex w-full items-center justify-center py-3">
            <span className="text-body font-body text-neutral-500">
              Loading more...
            </span>
          </div>
        )}
        {!hasMore && queries.length > 0 && !loading && (
          <div className="flex w-full items-center justify-center py-2">
            <span className="text-body font-body text-neutral-500">
              No more queries
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueriesListForDocument;

